import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { searchUsers } from '../../../services/knowAdmin';
import { removeFromOrg } from '../../../services/orgAdmin';
import EditUser from '../../editUser/EditUser';
import formatDate from '../../../utils/formatDate';
import styles from '../knowAdmin.module.css';

const propTypes = {
  admin: PropTypes.shape({
    _id: PropTypes.number.isRequired,
    role: PropTypes.string.isRequired,
  }).isRequired,
  orgs: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.number.isRequired,
      org_name: PropTypes.string.isRequired,
    })
  ).isRequired,
  messageCallback: PropTypes.func.isRequired,
};

export default function GlobalUserSearch({ admin, orgs, messageCallback }) {
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [userEdit, setUserEdit] = useState(null);
  const [modalState, setModalState] = useState(false);

  const orgNameById = orgs.reduce((acc, org) => {
    acc[org._id] = org.org_name;
    return acc;
  }, {});

  const runSearch = () => {
    setSearching(true);
    searchUsers(searchText)
      .then(({ data }) => {
        setResults(data);
        setHasSearched(true);
      })
      .catch((err) => messageCallback(err.response?.message ?? err.message))
      .finally(() => setSearching(false));
  };

  const handleSearch = (event) => {
    event.preventDefault();
    runSearch();
  };
  const handleSearchText = ({ target }) => setSearchText(target.value);
  const handleClear = () => {
    setSearchText('');
    setResults([]);
    setHasSearched(false);
  };

  const isSelf = (user) => Number(user._id) === Number(admin._id);

  const handleRemove = (user) => {
    if (isSelf(user)) {
      messageCallback('An admin cannot remove themself.');
      return;
    }
    const confirmation = window.confirm(
      'Are you sure you want to remove this user? This will immediately expire their access.'
    );
    if (!confirmation) {
      messageCallback('Remove cancelled.');
      return;
    }
    removeFromOrg(user._id, user.organizations)
      .then(() => {
        messageCallback('User removed.');
        runSearch();
      })
      .catch((err) => messageCallback(err.response?.message ?? err.message));
  };

  const handleEdit = (user) => {
    if (isSelf(user)) {
      messageCallback('An admin cannot edit themself.');
      return;
    }
    setUserEdit(user);
    setModalState(true);
  };

  const closeModalCallback = (editedUser) => {
    if (editedUser) {
      const userIndex = results.map((user) => user._id).indexOf(editedUser._id);
      if (userIndex !== -1) {
        const newResults = [...results];
        newResults.splice(userIndex, 1, editedUser);
        setResults(newResults);
      }
      messageCallback(`${editedUser.email} account details edited.`);
    }
    setUserEdit(null);
    setModalState(false);
  };

  const userInfo = results.map((user) => (
    <tr key={user._id}>
      <td>{user.first_name}</td>
      <td>{user.last_name}</td>
      <td>{user.email}</td>
      <td>{user.display_name}</td>
      <td>{user.role}</td>
      <td>
        {Array.isArray(user.memberships) && user.memberships.length > 0
          ? user.memberships.map((m) => m.orgName).join(', ')
          : (orgNameById[user.organizations] ?? user.org_name ?? '—')}
      </td>
      <td>{user.access_expiration ? formatDate(user.access_expiration) : '—'}</td>
      <td>{user.last_seen ? formatDate(user.last_seen) : '—'}</td>
      <td>
        <button type="button" onClick={() => handleRemove(user)}>
          Remove
        </button>
      </td>
      <td>
        <button type="button" onClick={() => handleEdit(user)}>
          Edit User
        </button>
      </td>
    </tr>
  ));

  const modalBlur = modalState ? { filter: 'blur(2px)' } : {};

  return (
    <>
      <EditUser
        key={userEdit?._id}
        user={userEdit}
        orgs={orgs}
        messageCallback={messageCallback}
        closeCallback={closeModalCallback}
      />
      <details className={styles.functionSection} style={modalBlur}>
        <summary>Global User Search</summary>
        <p>
          Search all registered users across every organization by email, first name, or last name.
          Results can be edited or removed directly from this table.
        </p>
        <form onSubmit={handleSearch}>
          <label htmlFor="globalUserSearch">Search users</label>
          <input
            id="globalUserSearch"
            type="text"
            value={searchText}
            onChange={handleSearchText}
            placeholder="Email, first name, or last name"
          />
          <button type="submit">Search</button>
          <button type="button" onClick={handleClear}>
            Clear Search
          </button>
        </form>
        {searching && <p>Searching...</p>}
        {hasSearched && !searching && (
          <>
            {results.length === 100 && (
              <p>Showing first 100 results — refine your search to narrow it down.</p>
            )}
            {!results.length && <p>No users found.</p>}
            {results.length > 0 && (
              <table>
                <thead>
                  <tr>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Display Name</th>
                    <th>Role</th>
                    <th>Organization</th>
                    <th>Access Expiration</th>
                    <th>Last Seen</th>
                    <th>Remove</th>
                    <th>Edit User</th>
                  </tr>
                </thead>
                <tbody>{userInfo}</tbody>
              </table>
            )}
          </>
        )}
      </details>
    </>
  );
}

GlobalUserSearch.propTypes = propTypes;
